import { useMemo } from "react";
import { subMonths } from "date-fns";

/** Sendbird上の表示保持期間（月）。CRMの CHAT_MESSAGE_RETENTION_MONTHS と同期 */
export const CHAT_MESSAGE_RETENTION_MONTHS = 5;

/** Sendbird InfiniteList の default loadThreshold と同期 */
export const SENDBIRD_LOAD_THRESHOLD = 0.05;

/** has_history 呼び出し前の messages 安定待ち（2回目リストの messageId を使うため） */
export const MESSAGE_LIST_SETTLE_MS = 300;

type MessageLike = {
  messageId: number;
  createdAt: number;
};

/** チャットメッセージ表示の下限タイムスタンプ（ミリ秒）を返す */
export const getChatMessageRetentionCutoffTimestamp = (): number => {
  return subMonths(new Date(), CHAT_MESSAGE_RETENTION_MONTHS).getTime();
};

/**
 * チャンネル単位で cutoff を固定する。
 * channelUrl が変わったときだけ now から再計算する。
 */
export const useChatMessageRetentionCutoff = (
  channelUrl: string | null | undefined,
): number => {
  return useMemo(
    () => getChatMessageRetentionCutoffTimestamp(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- チャンネル切替で cutoff を更新する
    [channelUrl],
  );
};

/**
 * 指定タイムスタンプのメッセージが表示対象かどうか。
 * cutoff を渡すとその値で判定する（チャンネル単位で固定した値を共有する用）。
 * 省略時は呼び出し時点の now から算出する。
 */
export const isChatMessageWithinRetentionPeriod = (
  createdAt?: number | null,
  cutoff?: number,
): boolean => {
  if (createdAt === undefined || createdAt === null) {
    return true;
  }
  const boundary = cutoff ?? getChatMessageRetentionCutoffTimestamp();
  return createdAt >= boundary;
};

/** 最古メッセージの createdAt。空配列なら null */
export const getOldestMessageCreatedAt = (
  messages: MessageLike[],
): number | null => {
  if (messages.length === 0) {
    return null;
  }
  return Math.min(...messages.map((message) => message.createdAt));
};

/** 保持期間内の先頭メッセージ ID（ボタンアンカー用） */
export const getFirstVisibleMessageId = (
  messages: MessageLike[],
  cutoff?: number,
): number | undefined => {
  const firstVisible = messages.find((message) =>
    isChatMessageWithinRetentionPeriod(message.createdAt, cutoff),
  );
  return firstVisible?.messageId;
};

/** 過去履歴画面のURLを組み立てる */
export const buildChatMessageHistoryUrl = (
  channelId: number,
  fromDate: number,
  messageId?: number,
): string => {
  let url = `/chat/message-history/${channelId}?from_date=${fromDate}`;
  if (messageId) {
    url += `&message_id=${messageId}`;
  }
  return url;
};

/** 保持期間到達後に scrollTop をこの値未満にさせない */
export const getMinScrollTop = (scrollElement: HTMLDivElement): number =>
  scrollElement.clientHeight * SENDBIRD_LOAD_THRESHOLD + 1;

/** Sendbird の useMessageLayoutDirection が dir を付け損ねた場合のフォールバック */
export const ensureSendbirdMessagesDir = (): void => {
  const elements = document.getElementsByClassName(
    "sendbird-conversation__messages",
  );
  Array.from(elements).forEach((element) => {
    const htmlElement = element as HTMLElement;
    if (!htmlElement.dir) {
      htmlElement.dir = "ltr";
    }
  });
};
